import type { ScaleLinear } from "d3-scale"
import { scaleLinear } from "d3-scale"

import type { ThickV1TickDistributionBin } from "../../tick-distribution"

/**
 * Pure helpers used by `RangeBrushView`. Kept in a separate module so the unit
 * tests in `t28` can exercise them without mounting React. All functions are
 * deterministic and free of side effects.
 */

/**
 * Find the largest `activeLiquidity` across all bins so we can normalise the
 * spline into the 0..1 y-range. Returns `0` for an empty set so callers can
 * branch on emptiness without a separate check.
 */
export function maxActiveLiquidity(bins: ReadonlyArray<ThickV1TickDistributionBin>): bigint {
  let max = 0n
  for (const bin of bins) {
    if (bin.activeLiquidity > max) max = bin.activeLiquidity
  }
  return max
}

/**
 * Align a tick to the nearest multiple of `tickSpacing` using the specified
 * rounding mode. `"nearest"` is the default, `"down"` and `"up"` clamp in the
 * respective direction so drag handles never cross each other mid-snap.
 */
export function snapToSpacing(
  tick: number,
  tickSpacing: number,
  direction: "down" | "up" | "nearest" = "nearest",
): number {
  if (tickSpacing <= 0) return tick
  const raw = tick / tickSpacing
  const steps =
    direction === "down" ? Math.floor(raw) : direction === "up" ? Math.ceil(raw) : Math.round(raw)
  const result = steps * tickSpacing
  // Normalise `-0` to `+0` so strict-equality assertions stay clean.
  return result === 0 ? 0 : result
}

export interface RangeBrushDomain {
  /** Inclusive lower bound of the chart domain in tick space. */
  tickLower: number
  /** Inclusive upper bound of the chart domain in tick space. */
  tickUpper: number
}

/**
 * Compute the tick-space viewport for the chart given the currently selected
 * range and the pool's current tick. We pad by 50% of the selected range on
 * each side so the handles always sit comfortably inside the frame, with a
 * minimum padding equal to 10 × `tickSpacing` so very narrow selections stay
 * visible. The domain is clamped inside the actual bins' span when bins exist,
 * avoiding blank regions.
 */
export function computeBrushDomain(args: {
  minTick: number
  maxTick: number
  currentTick: number | undefined
  bins: ReadonlyArray<ThickV1TickDistributionBin>
  tickSpacing: number
}): RangeBrushDomain {
  const { minTick, maxTick, currentTick, bins, tickSpacing } = args
  const range = Math.max(maxTick - minTick, tickSpacing)
  const padding = Math.max(range * 0.5, tickSpacing * 10)

  let lower = minTick - padding
  let upper = maxTick + padding

  if (currentTick !== undefined) {
    const halfSpan = Math.max((upper - lower) / 2, padding)
    lower = Math.min(lower, currentTick - halfSpan)
    upper = Math.max(upper, currentTick + halfSpan)
  }

  if (bins.length > 0) {
    const binLower = bins[0]!.tickLower
    const binUpper = bins[bins.length - 1]!.tickUpper
    lower = Math.max(lower, binLower)
    upper = Math.min(upper, binUpper)
  }

  if (upper <= lower) {
    upper = lower + tickSpacing
  }
  return { tickLower: lower, tickUpper: upper }
}

/**
 * Build the d3 x-scale that maps tick values to pixel coordinates. When
 * `invert` is true the scale's *range* is reversed so larger ticks render on
 * the left — used for the "price of token0 in token1" toggle.
 */
export function buildTickScale(args: {
  domain: RangeBrushDomain
  width: number
  invert: boolean
}): ScaleLinear<number, number> {
  const { domain, width, invert } = args
  const range: [number, number] = invert ? [width, 0] : [0, width]
  return scaleLinear().domain([domain.tickLower, domain.tickUpper]).range(range)
}

/**
 * Convert a pixel x-coordinate back to a tick value, then snap it to
 * `tickSpacing`. Used by the drag handlers. Callers guarantee ordering
 * (min < max) separately so this helper is free of cross-handle concerns.
 */
export function pixelToSnappedTick(args: {
  x: number
  scale: ScaleLinear<number, number>
  tickSpacing: number
}): number {
  const { x, scale, tickSpacing } = args
  const raw = scale.invert(x)
  return snapToSpacing(raw, tickSpacing, "nearest")
}

/**
 * Compute how many tick-spacings separate two ticks — used for the "N ticks"
 * indicator that floats next to the current-price line to give the user a
 * sense of how far the selected range sits from spot.
 */
export function tickDistanceInSpacings(args: {
  fromTick: number
  toTick: number
  tickSpacing: number
}): number {
  const { fromTick, toTick, tickSpacing } = args
  if (tickSpacing <= 0) return 0
  return Math.round((toTick - fromTick) / tickSpacing)
}
